import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { EmployeeStats } from '@/types/employee';
import {
  Briefcase,
  Calculator,
  CheckCircle2,
  Clock,
  Code,
  Crown,
  TrendingUp,
  Users,
} from 'lucide-react';
import { DEFAULT_FILTERS, type EmployeeFilters } from './filter-sidebar';

interface StatusCardsProps {
  stats: EmployeeStats | undefined;
  isLoading?: boolean;
  filters?: EmployeeFilters;
  onFilterChange?: (filters: EmployeeFilters) => void;
  /**
   * Right-side slot rendered inside the same bordered "view toolbar"
   * container. Used by the People route to host export / advanced-filter /
   * column-config / view-mode-toggle controls — promoting the row from a
   * pure filter rail to a unified "shape my view" toolbar.
   *
   * Visually separated from the chips by a `border-l` divider.
   */
  actions?: React.ReactNode;
}

/**
 * People page filter rail. Two semantic groups in one segmented container:
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │ ✓ Active 69 · ⏱ Probation 4  │  ♛ C-Level 4 · 💼 PM 2 · …  │ [tools]│
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Design intent:
 *  - Status icons (Active emerald, Probation amber) are slightly larger and
 *    always color-saturated regardless of selection → reads instantly as
 *    "current state of the workforce", not just filters.
 *  - Role chips stay neutral (`text-muted-foreground`) and only pick up the
 *    hygge accent when selected — they're tertiary information.
 *  - Active selection uses the app-wide `bg-hygge/10 text-hygge` token pair
 *    (same as side-nav `NavLink`) for consistent visual language.
 *  - Status group is divided from the role group by a 1-px hairline.
 *  - `actions` slot on the right hosts view tools (export, advanced filter,
 *    column config, view-mode toggle), separated by a `border-l` divider.
 */
export function StatusCards({
  stats,
  isLoading,
  filters,
  onFilterChange,
  actions,
}: StatusCardsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-1 rounded-lg border border-border/60 bg-card/40 p-1.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-20" />
        ))}
        {actions && (
          <div className="ml-auto flex items-center gap-2 border-l border-border/60 pl-2">
            {actions}
          </div>
        )}
      </div>
    );
  }

  const activeCount = stats?.active ?? 0;
  const probationCount = stats?.probation ?? 0;
  const categoryStats = stats?.positions ?? [];

  const categoryLabels: Record<string, string> = {
    cLevel: 'C-Level',
    projectManagers: 'PM',
    tech: 'Dev',
    hr: 'HR',
    finance: 'Accounting',
    sales: 'Sales',
  };

  const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
    cLevel: Crown,
    projectManagers: Briefcase,
    tech: Code,
    hr: Users,
    finance: Calculator,
    sales: TrendingUp,
  };

  const isCategoryFiltered = (positionIds: string[]) => {
    const currentPositionIds = filters?.positionIds || [];
    if (currentPositionIds.length === 0 || positionIds.length === 0) return false;
    return (
      currentPositionIds.length === positionIds.length &&
      currentPositionIds.every((id) => positionIds.includes(id)) &&
      positionIds.every((id) => currentPositionIds.includes(id))
    );
  };

  const isActiveFiltered =
    filters?.status === 'active' &&
    filters?.positionId === 'all' &&
    (!filters?.positionIds || filters.positionIds.length === 0);

  const isProbationFiltered = filters?.status === 'probation';

  const anyFilterActive =
    isActiveFiltered ||
    isProbationFiltered ||
    categoryStats.some((c) => isCategoryFiltered(c.positionIds));

  const handleActiveClick = () => {
    if (!onFilterChange || !filters) return;
    if (isActiveFiltered) {
      onFilterChange({ ...filters, status: DEFAULT_FILTERS.status });
    } else {
      onFilterChange({
        ...filters,
        status: 'active',
        positionId: 'all',
        positionIds: undefined,
      });
    }
  };

  const handleProbationClick = () => {
    if (!onFilterChange || !filters) return;
    if (isProbationFiltered) {
      onFilterChange({ ...filters, status: DEFAULT_FILTERS.status });
    } else {
      onFilterChange({
        ...filters,
        status: 'probation',
        positionId: 'all',
        positionIds: undefined,
      });
    }
  };

  const handleCategoryClick = (positionIds: string[]) => {
    if (!onFilterChange || !filters) return;
    if (isCategoryFiltered(positionIds)) {
      onFilterChange({ ...filters, positionIds: undefined });
    } else {
      onFilterChange({
        ...filters,
        status: 'current_staff',
        positionId: 'all',
        positionIds: positionIds,
      });
    }
  };

  const clearAll = () => {
    if (!onFilterChange || !filters) return;
    onFilterChange({
      ...filters,
      status: DEFAULT_FILTERS.status,
      positionId: 'all',
      positionIds: undefined,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-border/60 bg-card/40 p-1.5 transition-colors">
      {/* Status group — `tone` drives icon color + size so the status chips
          (Active, Probation) feel substantively different from the neutral
          role chips that follow. Always-on accent color makes the affordance
          read as "current state of the workforce" at a glance. */}
      <FilterChip
        icon={CheckCircle2}
        label="Active"
        count={activeCount}
        active={isActiveFiltered}
        onClick={handleActiveClick}
        tone="success"
      />
      {probationCount > 0 && (
        <FilterChip
          icon={Clock}
          label="Probation"
          count={probationCount}
          active={isProbationFiltered}
          onClick={handleProbationClick}
          tone="warning"
        />
      )}

      {/* Divider between status group and role group */}
      {categoryStats.length > 0 && (
        <div className="mx-1 h-5 w-px shrink-0 bg-border/80" aria-hidden="true" />
      )}

      {/* Role group --------------------------------------------------------- */}
      {categoryStats.map((category: { category: string; count: number; positionIds: string[] }) => {
        const Icon = categoryIcons[category.category];
        return (
          <FilterChip
            key={category.category}
            icon={Icon}
            label={categoryLabels[category.category] || category.category}
            count={category.count}
            active={isCategoryFiltered(category.positionIds)}
            onClick={() => handleCategoryClick(category.positionIds)}
          />
        );
      })}

      {/* Right-side cluster: inline "Clear" (when applicable) → divider →
          host-provided `actions` slot (export / filter / columns / view
          toggle on People page). `ml-auto` collapses the gap so chips stay
          left-aligned and the cluster floats right inside the same bordered
          "view toolbar" card. */}
      {(anyFilterActive || actions) && (
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {anyFilterActive && (
            <button
              type="button"
              onClick={clearAll}
              className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground cursor-pointer"
            >
              Clear
            </button>
          )}
          {actions && (
            <div className="ml-1 flex items-center gap-2 border-l border-border/60 pl-2">
              {actions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterChip
// ---------------------------------------------------------------------------

interface FilterChipProps {
  icon: React.ComponentType<{ className?: string }> | undefined;
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  /**
   * Visual emphasis tone:
   *   • `default` — neutral role chips (C-Level, PM, …). Icon stays muted
   *     when inactive, picks up the hygge accent when selected.
   *   • `success` — Active employees. Icon is always emerald, slightly
   *     larger, so the chip reads as a substantive workforce indicator
   *     rather than just another filter.
   *   • `warning` — Probationary employees. Same prominence treatment as
   *     `success` but in amber.
   *
   * The chip body styling (background, ring, hover) stays the same across
   * tones so the row reads as one cohesive control strip.
   */
  tone?: 'default' | 'success' | 'warning';
}

function FilterChip({ icon: Icon, label, count, active, onClick, tone = 'default' }: FilterChipProps) {
  const isStatusTone = tone === 'success' || tone === 'warning';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'group inline-flex shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-all duration-150',
        active
          ? // Matches `NavLink` and the rest of the app — instantly recognised
            // as "this thing is the currently-selected item".
            'border-hygge/30 bg-hygge/10 text-hygge shadow-sm'
          : // A faint hairline even unselected, so the chip reads as something
            // to press rather than as a label. [HW-926]
            'border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground'
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'shrink-0 transition-opacity',
            // Status tones get a bumped size so they outweigh the neutral role
            // chips visually.
            isStatusTone ? 'h-4 w-4' : 'h-3.5 w-3.5',
            // Unselected, every icon is the chip's muted grey. The status chips
            // used to keep a green or amber tint even then, which read as
            // "selected" on a page that opens with no chip chosen. [HW-926]
            active ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'
          )}
        />
      )}
      <span>{label}</span>
      <span
        className={cn(
          'tabular-nums text-[11px] font-semibold',
          active ? 'opacity-90' : 'opacity-50 group-hover:opacity-75'
        )}
      >
        {count}
      </span>
    </button>
  );
}
